import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Checkbox, DatePicker, Form, Input, Segmented, Select, Space, Tooltip, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";
import { type TeamMember } from "@fitness-app/data-models/entities/TeamMember";

import { ColumnVisibilityMenu } from "~/components/ColumnVisibilityMenu/ColumnVisibilityMenu";
import { useColumnVisibility } from "~/hooks/useColumnVisibility";
import { integrationApi, supabase } from "~/store/initializeStore";
import { AddMeetingModal } from "../components/AddMeetingModal";
import { applyBundleSelection, FeedbackModal } from "../components/FeedbackModal";
import { dateModeOptions, getRangePresets } from "../salesCrmConstants";
import { type SalesMeetingCapabilities } from "../salesCrmAccess";
import { pickLinkCandidates } from "../salesCrmUtils";
import { type FeedbackForm, type ManualMeetingForm } from "../salesCrmTypes";
import { useAddMeetingMutation } from "../hooks/useAddMeetingMutation";
import { useBundleOptionsQuery } from "../hooks/useBundleOptionsQuery";
import { useSaveFeedbackMutation } from "../hooks/useSaveFeedbackMutation";
import { useConfirmSellerManualPayment } from "../hooks/useConfirmSellerManualPayment";
import { usePendingManualOrdersQuery } from "../hooks/usePendingManualOrdersQuery";
import {
  useSalesMeetingsQuery,
  type SaleStatusFilterValue,
  type SalesMeetingRow,
  type SalesMeetingsDateMode,
} from "../hooks/useSalesMeetingsQuery";
import { type BlacklistPrefill } from "../SalesBlacklist";
import { salesMeetingsColumns, SalesMeetingsTable } from "./SalesMeetingsTable";

const { RangePicker } = DatePicker;

type SellerOption = { value: string; label: string };

type SalesMeetingsAccess = {
  canManageMeetings: boolean;
  canSelectSeller: boolean;
  canSyncMeetings: boolean;
  getMeetingCapabilities: (memberId: string | null) => SalesMeetingCapabilities;
  lockedManagedSellerId: string | null;
};

type SalesMeetingsTabProps = {
  isActive: boolean;
  range: [Dayjs, Dayjs];
  setRange: (value: [Dayjs, Dayjs]) => void;
  dateMode: SalesMeetingsDateMode;
  setDateMode: (value: SalesMeetingsDateMode) => void;
  clientEmail: string;
  setClientEmail: (value: string) => void;
  access: SalesMeetingsAccess;
  sellerOptions: SellerOption[];
  managedSellerOptions: SellerOption[];
  selectedSellerId: string | null;
  setSelectedSellerId: (value: string | null) => void;
  scopedSellerId: string | null;
  canQueryMeetings: boolean;
  selectedMember: TeamMember | null;
  userId: string | null | undefined;
  onBlacklist: (prefill: BlacklistPrefill) => void;
};

export const SalesMeetingsTab = ({
  isActive,
  range,
  setRange,
  dateMode,
  setDateMode,
  clientEmail,
  setClientEmail,
  access: { canManageMeetings, canSelectSeller, canSyncMeetings, getMeetingCapabilities, lockedManagedSellerId },
  sellerOptions,
  managedSellerOptions,
  selectedSellerId,
  setSelectedSellerId,
  scopedSellerId,
  canQueryMeetings,
  selectedMember,
  userId,
  onBlacklist,
}: SalesMeetingsTabProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const [selectedSaleStatusFilters, setSelectedSaleStatusFilters] = useState<SaleStatusFilterValue[]>([]);
  const { hiddenColumns, isColumnVisible, setHiddenColumns } = useColumnVisibility("salesCrmMeetings");
  // The sale-status filter lives in that column's header — hiding it must not
  // leave an invisible filter narrowing the list.
  const handleHiddenColumnsChange = (hidden: string[]) => {
    setHiddenColumns(hidden);
    if (hidden.includes("saleStatus")) setSelectedSaleStatusFilters([]);
  };
  const [postWarmupWithdrawalOnly, setPostWarmupWithdrawalOnly] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState<SalesMeetingRow | null>(null);
  const [bundleSearch, setBundleSearch] = useState("");
  const [isAddMeetingOpen, setIsAddMeetingOpen] = useState(false);
  const [prefillMeetingId, setPrefillMeetingId] = useState<string | null>(null);
  const [form] = Form.useForm<FeedbackForm>();
  const [addMeetingForm] = Form.useForm<ManualMeetingForm>();

  const meetingsTableQuery = useSalesMeetingsQuery({
    range,
    sellerId: scopedSellerId,
    saleStatusFilters: selectedSaleStatusFilters,
    paginated: true,
    queryScope: "table",
    enabled: canQueryMeetings && isActive,
    dateMode,
    emailSearch: clientEmail,
    postWarmupWithdrawalOnly,
  });

  const bundleOptionsQuery = useBundleOptionsQuery(activeMeeting, bundleSearch);

  // After adding a manual meeting, preselect the client's matching purchase so
  // the seller only confirms with save — an unlinked sale never reaches the
  // settlement, which is easy to miss when the select starts empty.
  useEffect(() => {
    const options = bundleOptionsQuery.data;

    if (!prefillMeetingId || !activeMeeting || activeMeeting.id !== prefillMeetingId || !options) {
      return;
    }

    setPrefillMeetingId(null);

    if (form.getFieldValue("customerBundleId")) {
      return;
    }

    const { bundleIds, orderValues } = pickLinkCandidates(options, activeMeeting.startTime);
    const meetingId = activeMeeting.id;

    void (async () => {
      let candidate: string | undefined;

      if (bundleIds.length) {
        // Skip bundles already confirmed on another meeting — double-linking
        // would count the same sale twice in settlements.
        const { data: taken } = await supabase
          .from("sales_meeting")
          .select("customerBundleId")
          .in("customerBundleId", bundleIds)
          .neq("id", meetingId);

        const takenIds = new Set((taken ?? []).map((row) => row.customerBundleId));
        candidate = bundleIds.find((id) => !takenIds.has(id));
      }

      candidate ??= orderValues[0];

      if (candidate && !form.getFieldValue("customerBundleId")) {
        applyBundleSelection(form, options, candidate);
      }
    })();
  }, [prefillMeetingId, activeMeeting, bundleOptionsQuery.data, form]);

  const openFeedback = (meeting: SalesMeetingRow, { prefillCandidate = false } = {}) => {
    setPrefillMeetingId(prefillCandidate ? meeting.id : null);
    setActiveMeeting(meeting);
    setBundleSearch("");
    form.setFieldsValue({
      status: meeting.status,
      saleStatus: meeting.saleStatus,
      customerBundleId: meeting.customerBundleId ?? meeting.matchedCustomerBundleId,
      notQualifiedReason: meeting.notQualifiedReason,
      notes: meeting.notes,
      manualAmount: null,
      manualTotalAmount: null,
      manualCycles: null,
      manualSaleDate: null,
    });
  };

  const saveFeedbackMutation = useSaveFeedbackMutation({
    activeMeeting,
    orders: bundleOptionsQuery.data?.orders ?? [],
    lockedTeamMemberId: lockedManagedSellerId,
    userId,
    onSuccess: () => {
      setActiveMeeting(null);
      form.resetFields();
    },
  });

  const addMeetingMutation = useAddMeetingMutation({
    lockedTeamMemberId: lockedManagedSellerId,
    onSuccess: (meeting, linkedBundleId) => {
      setIsAddMeetingOpen(false);
      addMeetingForm.resetFields();
      // The backend already linked a confident match — prefill only covers the
      // ambiguous/none case, with a human confirming the pick.
      openFeedback(meeting, { prefillCandidate: !linkedBundleId });
    },
  });

  const syncMeetingsMutation = useMutation({
    mutationFn: async () =>
      integrationApi.post("/calendly/sales-call/sync-meetings", {
        startTime: range[0].startOf("day").unix(),
        endTime: range[1].endOf("day").unix(),
      }),
    onSuccess: async ({ data }) => {
      await queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
      void message.success(
        `Sync spotkań: ${data.upsertedMeetings ?? 0} zapisanych, ${data.scheduledAttendanceTasks ?? 0} tasków obecności`,
      );
    },
    onError: (error) => {
      void message.error(`Nie udało się zsynchronizować spotkań: ${(error as Error).message}`);
    },
  });

  const tableMeetings = meetingsTableQuery.data?.list ?? [];

  // TRE-2006: przelewy z koszyka czekające na potwierdzenie — serwer zwraca
  // tylko zamówienia atrybuowane do spotkań zalogowanego sprzedawcy, więc dla
  // pozostałych ról mapa jest pusta i tabela niczego nie dorysowuje.
  // Bramka na `canManageMeetings` (= sales.manage): rola tylko-do-odczytu
  // dostałaby z serwera 403 przy każdym wejściu na zakładkę.
  const pendingManualOrdersQuery = usePendingManualOrdersQuery({
    meetings: tableMeetings,
    enabled: canQueryMeetings && isActive && canManageMeetings,
  });
  const confirmSellerManualPayment = useConfirmSellerManualPayment();
  const tableMeetingsInitialLoading = meetingsTableQuery.isInitialLoading;
  const manualSellerOptions = lockedManagedSellerId
    ? selectedMember?.id === lockedManagedSellerId
      ? [
          {
            value: selectedMember.id,
            label: `${selectedMember.firstName} ${selectedMember.lastName}`,
          },
        ]
      : []
    : managedSellerOptions;

  const openAddMeeting = () => {
    const selectedManagedSellerId = getMeetingCapabilities(selectedSellerId).canUpdate ? selectedSellerId : undefined;
    addMeetingForm.setFieldsValue({
      teamMemberId: lockedManagedSellerId ?? selectedManagedSellerId ?? managedSellerOptions[0]?.value,
      status: SalesMeetingStatus.Attended,
      startTime: dayjs(),
    });
    setIsAddMeetingOpen(true);
  };

  const closeAddMeeting = () => {
    setIsAddMeetingOpen(false);
    addMeetingForm.resetFields();
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      {meetingsTableQuery.isError ? (
        <Alert
          showIcon
          type="warning"
          message="Nie udało się pobrać przefiltrowanych spotkań"
          description={(meetingsTableQuery.error as Error).message}
        />
      ) : null}
      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <Space wrap align="center">
          <Segmented
            value={dateMode}
            onChange={(value) => setDateMode(value as SalesMeetingsDateMode)}
            options={dateModeOptions}
          />
          <RangePicker
            value={range}
            presets={getRangePresets()}
            onChange={(value) => value?.[0] && value?.[1] && setRange([value[0], value[1]])}
          />
          {canSelectSeller ? (
            <Select
              allowClear
              placeholder="Sprzedawca"
              value={selectedSellerId}
              onChange={(value) => setSelectedSellerId(value ?? null)}
              options={sellerOptions}
              style={{ width: 240 }}
            />
          ) : null}
          <Input.Search
            allowClear
            placeholder="Szukaj po mailu klienta"
            defaultValue={clientEmail}
            onSearch={(value) => setClientEmail(value.trim())}
            style={{ width: 260 }}
          />
          <Tooltip title="Klienci, którzy zrezygnowali już po zakończeniu okresu warmup — prowizja zostaje">
            <Checkbox
              checked={postWarmupWithdrawalOnly}
              onChange={(event) => setPostWarmupWithdrawalOnly(event.target.checked)}
            >
              Wycofani po warmupie
            </Checkbox>
          </Tooltip>
        </Space>
        <Space wrap>
          <ColumnVisibilityMenu
            columns={salesMeetingsColumns}
            hiddenColumns={hiddenColumns}
            onChange={handleHiddenColumnsChange}
          />
          {canManageMeetings ? (
            <Button type="primary" onClick={openAddMeeting} disabled={manualSellerOptions.length === 0}>
              Dodaj spotkanie ręcznie
            </Button>
          ) : null}
          {canSyncMeetings ? (
            <Button loading={syncMeetingsMutation.isLoading} onClick={() => syncMeetingsMutation.mutate()}>
              Synchronizuj spotkania z Calendly
            </Button>
          ) : null}
        </Space>
      </div>
      <SalesMeetingsTable
        access={{ getMeetingCapabilities }}
        isColumnVisible={isColumnVisible}
        loading={tableMeetingsInitialLoading}
        meetings={tableMeetings}
        onBlacklist={onBlacklist}
        onConfirmManualPayment={(order) =>
          confirmSellerManualPayment.mutate({ productId: order.productId, orderId: order.id })
        }
        confirmManualPaymentLoading={confirmSellerManualPayment.isLoading}
        pendingManualOrdersByEmail={pendingManualOrdersQuery.pendingByEmail}
        onEdit={openFeedback}
        onSaleStatusFiltersChange={setSelectedSaleStatusFilters}
        pagination={{
          count: meetingsTableQuery.data?.count ?? 0,
          currentPage: meetingsTableQuery.currentPage,
          pageSize: meetingsTableQuery.pageSize,
          pageSizeOptions: meetingsTableQuery.pageSizeOptions,
          setCurrentPage: meetingsTableQuery.setCurrentPage,
          showSizeChanger: meetingsTableQuery.showSizeChanger,
        }}
        selectedSaleStatusFilters={selectedSaleStatusFilters}
      />

      <AddMeetingModal
        open={isAddMeetingOpen}
        form={addMeetingForm}
        confirmLoading={addMeetingMutation.isLoading}
        sellerSelectionLocked={Boolean(lockedManagedSellerId)}
        sellerOptions={manualSellerOptions}
        onCancel={closeAddMeeting}
        onFinish={(values) => {
          // Each submit inserts a fresh uuid, so a double Enter would create
          // a real duplicate — confirmLoading only guards the OK button.
          if (!addMeetingMutation.isLoading) {
            addMeetingMutation.mutate(values);
          }
        }}
      />

      <FeedbackModal
        open={Boolean(activeMeeting)}
        form={form}
        confirmLoading={saveFeedbackMutation.isLoading}
        bundleOptions={bundleOptionsQuery.data}
        bundleOptionsLoading={bundleOptionsQuery.isLoading}
        onSearch={setBundleSearch}
        onCancel={() => setActiveMeeting(null)}
        onFinish={(values) => {
          // Selecting an order:-prefixed option inserts a fresh customer_bundle,
          // so a double Enter would create a duplicate — confirmLoading only
          // guards the OK button, not Enter-to-submit.
          if (!saveFeedbackMutation.isLoading) {
            saveFeedbackMutation.mutate(values);
          }
        }}
      />
    </Space>
  );
};
